import { Data, List, Model, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import StaticCurrencyLookup from '../../../Common/Models/StaticCurrencyLookup';
import { TextConstants } from '../../../Common/TextConstants';
import RawGenericTransactionLookup from '../../Transactions/Models/Lookups/RawGenericTransactionLookup';
import TransactionInterceptionSearchCriteria from '../../Transactions/Models/Queries/TransactionInterceptionSearchCriteria';
import SaveManualTransactionCommand from '../Models/Commands/SaveManualTransactionCommand';
import ManuallyCreatedTransactionLookup from '../Models/Lookups/ManuallyCreatedTransactionLookup';
import { StaticDataTypes } from '../../../Maintenance/StaticData/StaticDataTypes';
import StaticEventLookup from '../../EventMapping/Models/StaticEventSearchModal/StaticEventLookup';
import InstrumentSearchLookup from '../../InstrumentMapping/Models/InstrumentSearchLookup';
import ClientLookup from '../../ClientAccountMapping/Models/ClientLookup';

@NeoModel
export default class GenerateTransactionsVM extends Views.ViewModelBase {

    constructor(
        public eventApiClient = AppService.get(Types.ApiClients.StaticEventsApiClient),        
        public clientAccountApiClient = AppService.get(Types.ApiClients.ClientAccountApiClient),
        public exchangeApiClient = AppService.get(Types.ApiClients.ExchangeApiClient),
        public generalApiClient = AppService.get(Types.ApiClients.GeneralApiClient),
        public instrumentApiCient = AppService.get(Types.ApiClients.InstrumentApiClient),
        public transactionsApiClient = AppService.get(Types.ApiClients.TransactionsApiClient),
        public serviceProviderApiClient = AppService.get(Types.ApiClients.ServiceProviderApiClient),
        public taskRunner = AppService.get(Types.Neo.TaskRunner),
        private staticDataCache = AppService.get(StaticDataTypes.Services.StaticDataCache),
        private authService = AppService.get(Types.Services.AuthenticationService),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {
        super(taskRunner);
    }

    public transactionInterceptionSearchCriteria = new TransactionInterceptionSearchCriteria();
    public pendingTransactions = new List(ManuallyCreatedTransactionLookup);
    public transaction = new ManuallyCreatedTransactionLookup();
    public instrumentCurrencyLookupList: Model.PlainObject<StaticCurrencyLookup>[] = [];
    public accountCurrencyLookupList: Model.PlainObject<StaticCurrencyLookup>[] = [];
    public displayGenerateNewTransactionComponent: boolean = false;

    public events: Model.PlainObject<StaticEventLookup>[] = [];
    public filteredEvents: Model.PlainObject<StaticEventLookup>[] = [];
    public instruments: Model.PlainObject<InstrumentSearchLookup>[] = [];
    public filteredInstruments: Model.PlainObject<InstrumentSearchLookup>[] = [];
    public clientAccounts: Model.PlainObject<ClientLookup>[] = [];
    public filteredClientAccounts: Model.PlainObject<ClientLookup>[] = [];  

    public pageManager = new Data.PageManager(this.transactionInterceptionSearchCriteria, RawGenericTransactionLookup, this.transactionsApiClient.getTransactionInterceptionPagedList, {
        pageSize: 10,
        sortAscending: true,
        fetchInitial: false,
        initialTaskRunner: this.taskRunner,
    });


    public async initialise() {
        await Promise.all([
            this.getCurrenciesList(),
            this.populateEvents(),
            this.populateInstruments(),
            this.populateClientAccounts(),
        ]);
    }

    public async populateEvents() {
        this.events = await this.staticDataCache.events.getDataAsync()
            .then((arr) => arr.sort((x, y) => x.staticEventName.localeCompare(y.staticEventName)));
        this.filteredEvents = this.events.slice(0, 10);
    }

    public async populateInstruments() {
        this.instruments = await this.staticDataCache.instruments.getDataAsync()
            .then((arr) => arr.sort((x, y) => x.description!.localeCompare(y.description!)));
        this.filteredInstruments = this.instruments.slice(0, 10);
    }

    public async populateClientAccounts() {
        this.clientAccounts = await this.staticDataCache.clientAccounts.getDataAsync()
            .then((arr) => arr.sort((x, y) => x.clientDescription.localeCompare(y.clientDescription)));
        this.filteredClientAccounts = this.clientAccounts.slice(0, 10);
    }

    public showGenerateNewTransactionComponent() {
        this.displayGenerateNewTransactionComponent = true;
    }

    public async backToTransactionsPage() {
        this.displayGenerateNewTransactionComponent = false;
        this.transaction = new ManuallyCreatedTransactionLookup();
        await this.taskRunner.waitFor(this.pageManager.refreshData());
    }

    public async getCurrenciesList() {
        let response = await this.staticDataCache.staticCurrencies.getDataAsync();
        this.instrumentCurrencyLookupList = response;
        this.accountCurrencyLookupList = response;
    }

    public async populateClientDetails(clientAccountID: number) {
        var result = await this.clientAccountApiClient.getClientAccountLookupByID(clientAccountID);
        this.transaction.meta.rawAccountID.value = result.data.serviceProviderAccountNumber;
    }

    public async populateEventDetails(eventID: number) {
        var result = await this.eventApiClient.getEventLookupByID(eventID);
        if (result.data.isCorporateActionInd) {
            this.transaction.meta.eventType.value = TextConstants.Labels.CorporateAction;
            this.transaction.meta.corporateActionType.value = result.data.staticEventName;
        }
        else {
            this.transaction.meta.eventType.value = result.data.staticEventName;
        }
    }

    public async populateExchangeDetails(exchangeID: number) {
        var result = await this.exchangeApiClient.getExchangeLookupByID(exchangeID);
        this.transaction.meta.exchangeName.value = result.data.exchangeName;
        this.transaction.meta.exchangeMIC.value = result.data.mic;
    }

    public async populateInstrumentDetails(instrumentID: number) {
        var result = await this.instrumentApiCient.getInstrumentLookupByID(instrumentID);
        this.transaction.meta.securityCode.value = result.data.ticker!;
        this.transaction.meta.securityDescription.value = result.data.description!;
        this.transaction.meta.ISIN.value = result.data.isin!;
        this.transaction.meta.instrumentType.value = result.data.instrumentType!;
        this.transaction.meta.instrumentSubType.value = result.data.instrumentSubType!;
    }

    public async processPendingTransactions(transactions: List<ManuallyCreatedTransactionLookup>) {
        var command = new SaveManualTransactionCommand();
        const user = this.authService.user;
        command.manuallyCreatedTransactionLookups = this.pendingTransactions;
        command.userName = user?.displayName;

        // map pendingTransactions to transactionInterceptionSearchCriteria for the pageManager
        if (this.pendingTransactions.length !== 0) {
            const pendingTransaction = this.pendingTransactions[0];

            this.transactionInterceptionSearchCriteria.staticServiceProviderFileGroupingID = pendingTransaction.staticServiceProviderFileGroupingID;
            this.transactionInterceptionSearchCriteria.rawAccountNumber = pendingTransaction.rawAccountID;
            this.transactionInterceptionSearchCriteria.clientAccountNumber = pendingTransaction.clientAccountID.toString();
            this.transactionInterceptionSearchCriteria.serviceProviderGroupingName = pendingTransaction.serviceProviderGroupingName;
            this.transactionInterceptionSearchCriteria.instrument = pendingTransaction.ISIN;
            this.transactionInterceptionSearchCriteria.instrumentID = pendingTransaction.staticInstrumentID;
            this.transactionInterceptionSearchCriteria.eventName = pendingTransaction.eventType;
            this.transactionInterceptionSearchCriteria.rangeStartDate = pendingTransaction.dateTimeEvent;
            this.transactionInterceptionSearchCriteria.rangeEndDate = pendingTransaction.dateValue;
        }
        
        this.taskRunner.run(async () => {
            var response = await this.transactionsApiClient.createManualTransactions(command!.toJSObject());

            if (response.status === 200) {
                NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Validation.SuccessfullyGeneratedTransactions, NotificationDuration.Standard);
                this.pendingTransactions = new List(ManuallyCreatedTransactionLookup);

                await this.pageManager.refreshData();
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, TextConstants.Validation.FailGenerateTransactions, "danger" as any, NotificationDuration.Long);
            }
        })

        this.transactionInterceptionSearchCriteria = new TransactionInterceptionSearchCriteria();
    }

    public async saveTransaction() {
        this.pendingTransactions.push(this.transaction);
        this.displayGenerateNewTransactionComponent = false;
        this.transaction = new ManuallyCreatedTransactionLookup();
    }
}